import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSetores } from "@/hooks/use-setores";
import { type CorFita, type InsertColheita } from "@shared/schema";
import { Plus, Trash2, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { format } from "date-fns";

type ColheitaLine = {
  id: string;
  corFitaId: string | undefined;
  quantidadeCachos: number;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RegistrarColheitaDialog({ open, onOpenChange }: Props) {
  const { toast } = useToast();
  const [setorId, setSetorId] = useState("");
  const [dataColheita, setDataColheita] = useState(format(new Date(), "yyyy-MM-dd"));
  const [observacoes, setObservacoes] = useState("");
  const [semFita, setSemFita] = useState(false);
  const [quantidadeSemFita, setQuantidadeSemFita] = useState(1);
  const [lines, setLines] = useState<ColheitaLine[]>([
    { id: crypto.randomUUID(), corFitaId: undefined, quantidadeCachos: 1 },
  ]);

  const { setores } = useSetores({ somenteProdutivos: true });

  const { data: cores } = useQuery<CorFita[]>({
    queryKey: ["/api/cores"],
  });

  const { data: mangaras } = useQuery<any[]>({
    queryKey: ["/api/mangaras"],
  });

  const { data: colheitas } = useQuery<any[]>({
    queryKey: ["/api/colheitas"],
  });

  const { data: ciclos } = useQuery<any[]>({
    queryKey: ["/api/ciclos"],
  });

  const { data: perdas } = useQuery<any[]>({
    queryKey: ["/api/perdas"],
  });

  // Buscar parâmetro de tolerância para consistência com o servidor
  const { data: parametros } = useQuery<Array<{ chave: string; valor: string }>>({
    queryKey: ["/api/parametros"],
  });

  const toleranciaDias = useMemo(() => {
    const toleranciaParam = parametros?.find(p => p.chave === "TOLERANCIA_DIAS_COLHEITA");
    if (!toleranciaParam) return 15;
    const parsed = parseInt(toleranciaParam.valor);
    return isNaN(parsed) || parsed < 0 ? 15 : parsed;
  }, [parametros]);

  // Determinar quais cores devem ser mostradas
  // Mostrar cores ativas OU cores inativas que têm ciclos ativos associados
  const coresDisponiveis = useMemo(() => {
    if (!cores || !ciclos) return [];
    
    const ciclosAtivos = ciclos.filter((c: any) => c.status === 'ATIVO');
    const corIdsComCicloAtivo = new Set(ciclosAtivos.map((c: any) => c.corFitaId).filter(Boolean));
    
    return cores.filter(c => c.ativo || corIdsComCicloAtivo.has(c.id));
  }, [cores, ciclos]);

  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;

  // Calcular saldo disponível por cor de fita
  const saldoPorCor = useMemo(() => {
    if (!setorId || !mangaras || !colheitas || !ciclos || !perdas) return {};

    const saldos: Record<string, number> = {};

    // Filtrar apenas ciclos ATIVOS que têm mangarás
    const ciclosValidos = ciclos.filter((ciclo: any) => {
      if (ciclo.status !== 'ATIVO') return false;
      
      // Buscar mangarás deste ciclo
      const mangarasDoCiclo = mangaras.filter((m: any) => m.cicloId === ciclo.id);
      if (mangarasDoCiclo.length === 0) return false;

      return true;
    });

    const ciclosValidosIds = new Set(ciclosValidos.map((c: any) => c.id));

    // Para cada cor, calcular apenas de ciclos válidos
    cores?.forEach((cor) => {
      // Encontrar ciclos válidos com essa cor
      const ciclosDessaCor = ciclosValidos.filter((c: any) => c.corFitaId === cor.id);
      
      if (ciclosDessaCor.length === 0) {
        // Não incluir cor se não tem ciclo válido
        return;
      }

      const cicloIdsDessaCor = new Set(ciclosDessaCor.map((c: any) => c.id));

      // Contar mangarás dessa cor no setor em ciclos válidos
      // IMPORTANTE: A cor do mangarás vem do CICLO, não do mangarás diretamente
      const mangarasTotal = mangaras
        .filter((m: any) => 
          m.setorId === setorId && 
          cicloIdsDessaCor.has(m.cicloId)
        )
        .reduce((sum: number, m: any) => sum + (m.quantidadeCachos || 0), 0);

      // Contar colheitas dessa cor no setor em ciclos válidos
      // Colheitas TÊM corFitaId diretamente, então podemos usar
      const colheitasTotal = colheitas
        .filter((c: any) => 
          c.setorId === setorId && 
          c.corFitaId === cor.id && 
          ciclosValidosIds.has(c.cicloId)
        )
        .reduce((sum: number, c: any) => sum + (c.quantidadeCachos || 0), 0);

      // Contar perdas dessa cor no setor em ciclos válidos
      const perdasTotal = perdas
        .filter((p: any) => 
          p.setorId === setorId && 
          ciclosDessaCor.some((ciclo: any) => ciclo.id === p.cicloId)
        )
        .reduce((sum: number, p: any) => sum + (p.quantidadeCachos || 0), 0);

      const saldo = Math.max(0, mangarasTotal - colheitasTotal - perdasTotal);
      
      // Só incluir se tiver saldo > 0
      if (saldo > 0) {
        saldos[cor.id] = saldo;
      }
    });

    // Calcular saldo "sem fita" (apenas se houver dados)
    const mangarasSemFita = mangaras
      .filter((m: any) => m.setorId === setorId && !m.corFitaId && !m.cicloId)
      .reduce((sum: number, m: any) => sum + (m.quantidadeCachos || 0), 0);

    const colheitasSemFita = colheitas
      .filter((c: any) => c.setorId === setorId && !c.corFitaId)
      .reduce((sum: number, c: any) => sum + (c.quantidadeCachos || 0), 0);

    const saldoSemFita = Math.max(0, mangarasSemFita - colheitasSemFita);
    if (saldoSemFita > 0) {
      saldos["_NONE_"] = saldoSemFita;
    }

    return saldos;
  }, [setorId, mangaras, colheitas, ciclos, perdas, cores]);

  // Encontrar data mínima permitida para colheita (data do mangarás mais antigo com saldo)
  const dataMinima = useMemo(() => {
    if (!setorId || !mangaras) return null;

    const mangarasDoSetor = mangaras.filter((m: any) => m.setorId === setorId);
    if (mangarasDoSetor.length === 0) return null;

    // Encontrar o mangarás mais antigo
    const maisAntigo = mangarasDoSetor.reduce((oldest: any, current: any) => {
      return new Date(current.dataCorteMangara) < new Date(oldest.dataCorteMangara) ? current : oldest;
    });

    return maisAntigo.dataCorteMangara;
  }, [setorId, mangaras]);

  const createColheitasMutation = useMutation({
    mutationFn: async (colheitas: InsertColheita[]) => {
      const results = await Promise.all(
        colheitas.map(async (data) => {
          const response = await apiRequest("POST", "/api/colheitas", data);
          return await response.json();
        })
      );
      return results;
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: ["/api/colheitas"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      toast({
        title: `${results.length} colheita(s) registrada(s)!`,
        description: "As colheitas foram associadas automaticamente aos ciclos corretos.",
      });
      handleReset();
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao registrar colheitas",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleAddLine = () => {
    setLines([...lines, { id: crypto.randomUUID(), corFitaId: undefined, quantidadeCachos: 1 }]);
  };

  const handleRemoveLine = (id: string) => {
    if (lines.length > 1) {
      setLines(lines.filter((line) => line.id !== id));
    }
  };

  const handleLineChange = (id: string, field: keyof Omit<ColheitaLine, "id">, value: string | number | undefined) => {
    setLines(lines.map((line) =>
      line.id === id ? { ...line, [field]: value } : line
    ));
  };

  const handleSubmit = () => {
    if (!setorId || !dataColheita) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha o setor e a data da colheita.",
        variant: "destructive",
      });
      return;
    }

    // Se é "Sem fita", registrar diretamente sem validar saldo
    if (semFita) {
      if (quantidadeSemFita < 1) {
        toast({
          title: "Quantidade inválida",
          description: "A quantidade deve ser maior que zero.",
          variant: "destructive",
        });
        return;
      }

      const colheitaSemFita: InsertColheita = {
        setorId,
        corFitaId: null as any,
        dataColheita,
        quantidadeCachos: quantidadeSemFita,
        semFita: true,
        observacoes: observacoes || undefined,
        usuarioId: user?.id || "",
      };

      createColheitasMutation.mutate([colheitaSemFita]);
      return;
    }

    // Validar data mínima (apenas para colheitas com fita)
    if (dataMinima && new Date(dataColheita) < new Date(dataMinima)) {
      toast({
        title: "Data inválida",
        description: `A data de colheita não pode ser anterior à data do mangarás mais antigo (${format(new Date(dataMinima), "dd/MM/yyyy")}).`,
        variant: "destructive",
      });
      return;
    }

    const validLines = lines.filter((line) => line.quantidadeCachos > 0);
    if (validLines.length === 0) {
      toast({
        title: "Nenhuma linha válida",
        description: "Adicione pelo menos uma linha com quantidade.",
        variant: "destructive",
      });
      return;
    }

    // Validar saldo disponível agregando quantidades por cor
    const quantidadesPorCor: Record<string, number> = {};
    for (const line of validLines) {
      const corKey = line.corFitaId || "_NONE_";
      quantidadesPorCor[corKey] = (quantidadesPorCor[corKey] || 0) + line.quantidadeCachos;
    }

    for (const [corKey, quantidadeTotal] of Object.entries(quantidadesPorCor)) {
      const saldoDisponivel = saldoPorCor[corKey] || 0;
      
      if (quantidadeTotal > saldoDisponivel) {
        const corNome = corKey === "_NONE_"
          ? "Sem fita"
          : cores?.find(c => c.id === corKey)?.nome || "Desconhecida";
        
        toast({
          title: "Saldo insuficiente",
          description: `A cor "${corNome}" tem apenas ${saldoDisponivel} cachos disponíveis. Você está tentando colher ${quantidadeTotal} cachos no total.`,
          variant: "destructive",
        });
        return;
      }
    }

    const colheitas: InsertColheita[] = validLines.map((line) => ({
      setorId,
      corFitaId: line.corFitaId || null as any,
      dataColheita,
      quantidadeCachos: line.quantidadeCachos,
      semFita: false,
      observacoes: observacoes || undefined,
      usuarioId: user?.id || "",
    }));

    createColheitasMutation.mutate(colheitas);
  };

  const handleReset = () => {
    setSetorId("");
    setDataColheita(format(new Date(), "yyyy-MM-dd"));
    setObservacoes("");
    setSemFita(false);
    setQuantidadeSemFita(1);
    setLines([{ id: crypto.randomUUID(), corFitaId: undefined, quantidadeCachos: 1 }]);
  };

  const selectedSetor = setores?.find((s) => s.id === setorId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Registrar Colheita</DialogTitle>
          <DialogDescription>
            Selecione o setor e data, depois adicione as cores de fita e quantidades.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Setor e Data */}
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="setorId">Setor *</Label>
              <Select value={setorId} onValueChange={setSetorId}>
                <SelectTrigger data-testid="select-setor">
                  <SelectValue placeholder="Selecione o setor" />
                </SelectTrigger>
                <SelectContent>
                  {setores?.filter(s => s.ativo).sort((a, b) => a.nome.localeCompare(b.nome)).map((setor) => (
                    <SelectItem key={setor.id} value={setor.id}>
                      {setor.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSetor?.variedade && (
                <p className="text-xs text-muted-foreground">
                  Variedade: <span className="font-medium">{selectedSetor.variedade.nome}</span>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dataColheita">Data da Colheita *</Label>
              <Input
                id="dataColheita"
                type="date"
                className="font-mono"
                min={dataMinima || undefined}
                value={dataColheita}
                onChange={(e) => setDataColheita(e.target.value)}
                data-testid="input-data-colheita"
              />
              {dataMinima && (
                <p className="text-xs text-muted-foreground">
                  Data mínima: {format(new Date(dataMinima), "dd/MM/yyyy")} (mangarás mais antigo)
                </p>
              )}
            </div>
          </div>

          {/* Toggle Sem Fita */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
            <div className="space-y-1">
              <Label htmlFor="sem-fita" className="text-base font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-atencao" />
                Sem fita
              </Label>
              <p className="text-sm text-muted-foreground">
                Ative se a fita de identificação foi perdida durante a colheita
              </p>
            </div>
            <Switch
              id="sem-fita"
              checked={semFita}
              onCheckedChange={setSemFita}
              data-testid="switch-sem-fita"
            />
          </div>

          {/* Modo Sem Fita - Campo de quantidade simples */}
          {semFita && (
            <>
              <Alert variant="default" className="border-atencao/50 bg-atencao-suave/50">
                <AlertTriangle className="h-4 w-4 text-atencao" />
                <AlertDescription className="text-atencao">
                  Colheita sem identificação de fita. Esses cachos serão registrados sem associação a um ciclo de produção.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <Label htmlFor="quantidade-sem-fita">Quantidade de Cachos *</Label>
                <Input
                  id="quantidade-sem-fita"
                  type="number"
                  min="1"
                  value={quantidadeSemFita === 0 ? "" : quantidadeSemFita}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setQuantidadeSemFita(0);
                    } else {
                      setQuantidadeSemFita(parseInt(value) || 0);
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.target.value || parseInt(e.target.value) < 1) {
                      setQuantidadeSemFita(1);
                    }
                  }}
                  className="max-w-xs"
                  data-testid="input-quantidade-sem-fita"
                />
              </div>
            </>
          )}

          {/* Saldo Disponível - Apenas quando não é "sem fita" */}
          {!semFita && setorId && Object.keys(saldoPorCor).length > 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">Saldo disponível de cachos para colheita:</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {coresDisponiveis
                    ?.filter(c => saldoPorCor[c.id] !== undefined)
                    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                    .map((cor) => (
                      <div key={cor.id} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded border flex-shrink-0"
                          style={{ backgroundColor: cor.hexColor }}
                        />
                        <span className="font-medium">{cor.nome}:</span>
                        <span className={`font-mono font-semibold ${saldoPorCor[cor.id] === 0 ? 'text-destructive' : ''}`}>
                          {saldoPorCor[cor.id]} cachos
                        </span>
                      </div>
                    ))}
                  {saldoPorCor["_NONE_"] !== undefined && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Sem fita:</span>
                      <span className={`font-mono font-semibold ${saldoPorCor["_NONE_"] === 0 ? 'text-destructive' : ''}`}>
                        {saldoPorCor["_NONE_"]} cachos
                      </span>
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Tabela de Cores e Quantidades - Apenas quando não é "sem fita" */}
          {!semFita && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Cores de Fita e Quantidades *</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddLine}
                data-testid="button-add-line"
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Linha
              </Button>
            </div>

            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cor da Fita</TableHead>
                    <TableHead className="w-40">Quantidade</TableHead>
                    <TableHead className="w-32">Saldo</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, index) => {
                    const selectedCor = cores?.find((c) => c.id === line.corFitaId);
                    const corKey = line.corFitaId || "_NONE_";
                    const saldoDisponivel = saldoPorCor[corKey] || 0;
                    const hasError = line.corFitaId !== undefined && line.quantidadeCachos > saldoDisponivel;
                    
                    return (
                      <TableRow key={line.id}>
                        <TableCell>
                          <Select
                            value={line.corFitaId || "_NONE_"}
                            onValueChange={(value) => handleLineChange(line.id, "corFitaId", value === "_NONE_" ? null as any : value)}
                          >
                            <SelectTrigger data-testid={`select-cor-${index}`}>
                              <SelectValue placeholder="Sem fita / Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_NONE_">Sem fita</SelectItem>
                              {coresDisponiveis?.filter(c => saldoPorCor[c.id] !== undefined).sort((a, b) => (a.ordem || 0) - (b.ordem || 0)).map((cor) => (
                                <SelectItem key={cor.id} value={cor.id}>
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-4 h-4 rounded border"
                                      style={{ backgroundColor: cor.hexColor }}
                                    />
                                    <span>{cor.nome}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            max={saldoDisponivel > 0 ? saldoDisponivel : undefined}
                            value={line.quantidadeCachos === 0 ? "" : line.quantidadeCachos}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                handleLineChange(line.id, "quantidadeCachos", 0);
                              } else {
                                handleLineChange(line.id, "quantidadeCachos", parseInt(value) || 0);
                              }
                            }}
                            onBlur={(e) => {
                              if (!e.target.value || parseInt(e.target.value) < 1) {
                                handleLineChange(line.id, "quantidadeCachos", 1);
                              }
                            }}
                            className={hasError ? "border-destructive" : ""}
                            data-testid={`input-quantidade-${index}`}
                          />
                        </TableCell>
                        <TableCell>
                          {line.corFitaId !== undefined && (
                            <div className={`text-sm font-mono ${hasError ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                              {saldoDisponivel} disp.
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveLine(line.id)}
                            disabled={lines.length === 1}
                            data-testid={`button-remove-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          )}

          {/* Observações */}
          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações Gerais</Label>
            <Textarea
              id="observacoes"
              placeholder="Observações sobre as colheitas (aplicadas a todos os registros)..."
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              data-testid="input-observacoes"
            />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse md:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-cancel"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createColheitasMutation.isPending}
            data-testid="button-submit"
          >
            {createColheitasMutation.isPending ? "Salvando..." : "Registrar Todas"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
